import { Grid2 as Grid, LinearProgress, Stack, Typography, TypographyOwnProps } from '@mui/material';
import PlanningWrapper from './Planning/PlanningWrapper';
import DemoWrapper from './Demo/DemoWrapper';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimelapseSharpIcon from '@mui/icons-material/TimelapseSharp';
import {
  CycleActivity,
  CYCLE_ACTIVITY_STATUS,
  CYCLE_ACTIVITY_STATUS_LABELS,
  ProjectSectionAccordion,
  useProjectDataContext,
} from 'domain/shared/newProject';
import { useDateService } from 'framework/utils';

const CycleActivityAccordion = () => {
  const { data } = useProjectDataContext();
  const { getMomentDateObj, getCurrentDateTime, getDaysBetween } = useDateService();

  const calculateProgress = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
      return { progress: 0, daysPassed: 0, isActive: false };
    }

    const start = getMomentDateObj(startDate).startOf('day');
    const end = getMomentDateObj(endDate).endOf('day');
    const now = getCurrentDateTime();

    if (now.isBefore(start) || now.isAfter(end)) {
      return { progress: 0, daysPassed: 0, isActive: false };
    }

    const totalDuration = end.diff(start, 'days');
    const elapsed = getDaysBetween(now, start) - 1;
    const progress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

    return { progress, daysPassed: elapsed, isActive: true };
  };

  const cycles: CycleActivity[] = data?.cycleActivities ?? [];

  if (cycles.length === 0) {
    return null;
  }

  const getCycleStatusIcon = (cycle: CycleActivity, isActive: boolean) => {
    if (isActive) {
      return null;
    }

    // Completed cycle with both planning and demo held
    if (
      cycle?.status === CYCLE_ACTIVITY_STATUS.COMPLETED &&
      cycle?.planning?.planningDate &&
      cycle?.demo?.demoDate &&
      !cycle?.planning?.isSkipped &&
      !cycle?.demo?.isSkipped
    ) {
      return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    }

    // Completed cycle with one event skipped
    if (
      cycle?.status === CYCLE_ACTIVITY_STATUS.COMPLETED &&
      ((cycle?.planning?.isSkipped && !cycle?.demo?.isSkipped) ||
        (!cycle?.planning?.isSkipped && cycle?.demo?.isSkipped))
    ) {
      return <NotificationImportantIcon sx={{ color: 'warning.dark' }} />;
    }

    // Skipped cycle
    if (cycle?.status === CYCLE_ACTIVITY_STATUS.SKIPPED) {
      return <CancelIcon sx={{ color: 'error.main' }} />;
    }

    // Expired cycle
    if (cycle?.status === CYCLE_ACTIVITY_STATUS.EXPIRED) {
      return <TimelapseSharpIcon sx={{ color: 'text.secondary' }} />;
    }

    return null;
  };

  const getCycleStatusMessage = (cycle: CycleActivity) => {
    if (cycle?.status === CYCLE_ACTIVITY_STATUS.COMPLETED) {
      if (cycle.planning?.isSkipped && !cycle.demo?.isSkipped) {
        return 'Planning was skipped, but the demo was held';
      }
      if (!cycle.planning?.isSkipped && cycle.demo?.isSkipped) {
        return 'Demo was skipped, but the planning was held';
      }
    }

    if (cycle?.status === CYCLE_ACTIVITY_STATUS.SKIPPED) {
      return 'Planning and demo were skipped';
    }

    return null;
  };

  const getCycleStatusColor = (status: string): TypographyOwnProps['color'] => {
    switch (status) {
      case CYCLE_ACTIVITY_STATUS.IN_PROGRESS:
        return 'warning';
      case CYCLE_ACTIVITY_STATUS.COMPLETED:
        return 'success';
      case CYCLE_ACTIVITY_STATUS.SKIPPED:
        return 'error';
      default:
        return 'textSecondary';
    }
  };

  return (
    <>
      {cycles.map(cycle => {
        const progressData = calculateProgress(cycle.startDate, cycle.endDate);
        const statusIcon = getCycleStatusIcon(cycle, progressData.isActive);
        const statusMessage = getCycleStatusMessage(cycle);

        return (
          <ProjectSectionAccordion
            key={cycle.id}
            sx={{
              '&.Mui-expanded': {
                m: 0,
              },
              m: 0,
            }}
            defaultExpanded={progressData.isActive}
            title={
              <Grid
                container
                spacing={1}
                size={12}
                sx={{
                  width: '100%',
                  pl: 2,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Grid size={3}>
                  <Typography variant="body1" color={cycle?.title ? 'primary' : 'textSecondary'}>
                    {cycle?.title ?? "Cycle's dates will be available soon"}
                  </Typography>
                </Grid>

                <Grid size={3} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                  {progressData.isActive ? (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={progressData.progress}
                        sx={{ height: 8, borderRadius: 1, width: '30%' }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        {progressData.daysPassed} {progressData.daysPassed === 1 ? 'day' : 'days'} passed
                      </Typography>
                    </>
                  ) : (
                    statusIcon
                  )}
                </Grid>

                <Grid size={4}>{statusMessage && <Typography variant="body2">{statusMessage}</Typography>}</Grid>

                <Grid size={2} display="flex" justifyContent="flex-end">
                  <Typography color={getCycleStatusColor(cycle?.status)} variant="body1">
                    {CYCLE_ACTIVITY_STATUS_LABELS[cycle?.status] || CYCLE_ACTIVITY_STATUS_LABELS.NOT_STARTED}
                  </Typography>
                </Grid>
              </Grid>
            }
          >
            <Stack>
              <PlanningWrapper cycle={cycle} data={data} />
              <DemoWrapper cycle={cycle} data={data} />
            </Stack>
          </ProjectSectionAccordion>
        );
      })}
    </>
  );
};

export default CycleActivityAccordion;
